/**
 * @file OurApproach.jsx
 * @description Our Approach page with proposal boilerplate content
 */

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import HeroImage from '../new_images/pexels-kampus-8636589.jpg';
import Image1 from '../new_images/pexels-mikhail-nilov-9304659.jpg';
import Image2 from '../new_images/pexels-tara-winstead-8386437.jpg';
import Image3 from '../new_images/pexels-vlada-karpovich-7433832.jpg';
import Image4 from '../new_images/pexels-yankrukov-7691673.jpg';
import Image5 from '../new_images/pexels-yankrukov-7691715.jpg';

const OurApproach = () => {
  return (
    <div>
      <Helmet>
        <title>Our Approach | Bellefy Insights</title>
        <meta
          name="description"
          content="Learn about Bellefy Insights' approach to data and analytics - decision-driven design, end-to-end implementation, and sustainable solutions."
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        id="intro" 
        style={{ 
          paddingTop: '150px', 
          paddingBottom: '100px', 
          minHeight: 'auto',
          backgroundImage: `linear-gradient(111deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${HeroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundAttachment: 'fixed'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={10}>
              <h1 className="text-center" style={{ color: '#ffffff', marginBottom: '1rem' }}>
                Our Approach
              </h1>
              <p className="text-center lead" style={{ color: '#ffffff' }}>
                Implementation-Led Data & Decision Intelligence
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Proposal Boilerplate Section */}
      <section id="proposal" className="section-bg">
        <Container>
          <Row className="justify-content-center align-items-center">
            <Col md={10}>
              <div className="mt-5">
                <Row className="align-items-center mb-4">
                  <Col md={5} className="mb-4 mb-md-0">
                    <img src={Image1} alt="Data and decision-making" className="img-fluid rounded shadow" style={{ width: '100%', objectFit: 'cover' }} />
                  </Col>
                  <Col md={7}>
                    <h3>Understanding of the Assignment</h3>
                    <p>
                      Bellefy Insights understands that the core challenge is not the absence of data, but the difficulty
                      of turning existing data into timely, reliable insight that supports decision-making.
                      Organizations often operate across multiple data sources, teams, and reporting requirements,
                      resulting in fragmented systems, manual processes, and underutilized dashboards. Data chaos slows down impact.
                    </p>
                    <p>
                      This assignment requires an implementation-led approach that connects data, systems, and decision
                      needs. We help organizations cut through the noise by building systems that bring all your data together in one place you can trust.
                    </p>
                  </Col>
                </Row>
              </div>

              <div className="mt-5">
                <h3>Our Approach</h3>
                <p>Our approach is structured around four principles, with a strong focus on adoption and sustainability:</p>
                <Row className="mt-3 align-items-center">
                  <Col md={6} className="mb-4">
                    <Row>
                      <Col xs={12} className="mb-3">
                        <h4>1. Decision-Driven Design</h4>
                        <p>We begin by identifying the decisions, users, and performance questions the system must support. Good decisions start with good data.</p>
                      </Col>
                      <Col xs={12} className="mb-3">
                        <h4>2. End-to-End Implementation</h4>
                        <p>We take responsibility from data ingestion and modeling through dashboards and performance frameworks. From strategy to execution, we handle the heavy lifting.</p>
                      </Col>
                      <Col xs={12} className="mb-3">
                        <h4>3. Practical and Scalable Solutions</h4>
                        <p>Systems are designed to fit organizational capacity and grow over time. We co-design with local teams to ensure solutions work in your context.</p>
                      </Col>
                      <Col xs={12}>
                        <h4>4. Adoption and Sustainability</h4>
                        <p>Beyond delivering tools, we prioritise adoption through hands-on capacity building, documentation, and handover to ensure long-term value and use.</p>
                      </Col>
                    </Row>
                  </Col>
                  <Col md={6} className="mb-4">
                    <img src={Image2} alt="Our approach to data and analytics" className="img-fluid rounded shadow" style={{ width: '100%', objectFit: 'cover' }} />
                  </Col>
                </Row>
              </div>

              <div className="mt-5">
                <Row className="align-items-start">
                  <Col md={6}>
                    <h3>Scope of Work</h3>
                    <p>Depending on the engagement, Bellefy Insights will:</p>
                    <ul className="mt-3">
                      <li>Conduct a rapid diagnostic of existing data systems and reporting workflows</li>
                      <li>Clean and consolidate scattered data sources, migrating systems to secure cloud platforms</li>
                      <li>Design and implement automated data pipelines and analytics pipelines</li>
                      <li>Build sophisticated dashboards and responsible AI agents that automate repetitive work</li>
                      <li>Develop monitoring and evaluation frameworks, from baselines to endline evaluations</li>
                      <li>Create clear Theories of Change and practical frameworks aligned to your mission</li>
                      <li>Support documentation, hands-on capacity building, training, and handover</li>
                      <li>Set up human-in-the-loop controls, audit trails and privacy safeguards</li>
                    </ul>
                  </Col>
                  <Col md={6} className="mt-4 mt-md-0">
                    <img src={Image3} alt="Scope of work - data and technology" className="img-fluid rounded shadow" style={{ width: '100%', objectFit: 'cover' }} />
                  </Col>
                </Row>
              </div>

              <div className="mt-5">
                <Row className="align-items-center">
                  <Col md={5} className="mb-4 mb-md-0 order-md-2">
                    <img src={Image4} alt="Expected outcomes - impact and insights" className="img-fluid rounded shadow" style={{ width: '100%', objectFit: 'cover' }} />
                  </Col>
                  <Col md={7} className="order-md-1">
                    <h3>Expected Outcomes</h3>
                    <ul className="mt-3">
                      <li>Reduced manual reporting and duplication - spend less time chasing numbers and more time driving change</li>
                      <li>Improved data quality and consistency - information that is accurate, accessible, and meaningful</li>
                      <li>Increased visibility into performance and outcomes - track what truly matters and demonstrate real value</li>
                      <li>Stronger evidence-based decision-making - insights are trusted and decisions improve</li>
                      <li>Faster donor reporting and smarter service delivery</li>
                      <li>Measurable impact through trusted data and pragmatic automation</li>
                    </ul>
                  </Col>
                </Row>
              </div>

              <div className="mt-5">
                <Row className="align-items-center">
                  <Col md={6} className="mb-4 mb-md-0">
                    <img src={Image5} alt="Why Bellefy Insights" className="img-fluid rounded shadow" style={{ width: '100%', objectFit: 'cover' }} />
                  </Col>
                  <Col md={6}>
                    <h3>Why Bellefy Insights</h3>
                    <p>
                      Bellefy Insights is a mission-driven data and automation practice founded by practitioners who've managed complex datasets across development and government programs. We combine technical execution with analytical rigor to deliver data systems that are not only well-designed, but actively used.
                    </p>
                    <p>
                      Our focus on implementation and adoption ensures that insight translates into action. We turn complex text data into actionable insights, transforming raw information into guidance for smarter action. Our vision is a future where data actively drives organizational success, innovation, and continuous improvement.
                    </p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default OurApproach;
